<script lang="ts" setup>
import { email } from '@vee-validate/rules';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Form } from 'vee-validate';
import { computed, ref } from 'vue';

import notify from '@/api/notify';
import EmailTypoSuggestion from '@/components/base/form/EmailTypoSuggestion.vue';
import FormInput, { InputAttributes } from '@/components/base/form/FormInput.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import { useCallback } from '@/composables/useCallback';
import { FlexibleMessageValidator, useForm } from '@/composables/useForm';
import { useCustomer } from '@/stores/customer';
import { getFormattedDisplayDate } from '@/utils/autoDelivery';

dayjs.extend(relativeTime);

interface EmailStepData {
  email: string;
}

const props = defineProps<{
  productKey: string;
  backorderedUntil?: string;
}>();

const emailValue = ref(useCustomer().customer?.email ?? '');

const { errorMessages, validatorFailed } = useForm();

const formInputAttributes: Record<keyof EmailStepData, InputAttributes> = {
  email: {
    autocomplete: 'email',
    name: 'email',
    placeholder: 'Enter your email address',
    type: 'email',
  },
};

const rules: Record<keyof EmailStepData, FlexibleMessageValidator<string>> = {
  email: (value) => {
    if (!email(value)) return errorMessages.email.email;
    return true;
  },
};

const notificationSent = ref(false);

const currentDate = dayjs().startOf('day');

const backorderedNotice = computed(() => {
  const notice = 'Sorry, this item is out of stock.';

  if (!props.backorderedUntil || props.backorderedUntil === '') {
    return notice;
  }

  const backorderedDate = dayjs(props.backorderedUntil);

  if (!backorderedDate) {
    return notice;
  }

  if (!backorderedDate.isAfter(currentDate)) {
    return notice;
  }

  const relativeFromNow = `${backorderedDate.from(currentDate, true)} from now`.replace(
    'a day from now',
    'tomorrow',
  );

  const formatedDate = getFormattedDisplayDate(props.backorderedUntil);

  return `Out of stock until ${formatedDate} (${relativeFromNow}).`;
});

const sendNotification = async () => {
  if (emailValue.value !== '' && rules.email(emailValue.value) === true) {
    const response = await notify.restockNotification(emailValue.value, props.productKey);

    if (response.status === 200) {
      notificationSent.value = true;
    }
  }
};

const sendNotificationCallback = useCallback(sendNotification);
</script>

<template>
  <div>
    <transition name="fade" mode="out-in">
      <div v-if="!notificationSent">
        <p class="text-base font-bold" id="standard-backordered-notice">{{ backorderedNotice }}</p>
        <p v-once id="dy-backordered-override" />
        <div class="w-full">
          <Form
            v-slot="{ meta: formEmailMeta }"
            class="flex w-full h-full"
            @submit="sendNotificationCallback.execute()"
          >
            <div class="relative w-full h-full">
              <FormInput
                v-model="emailValue"
                aria-label="email"
                class="w-full h-full"
                :inputAttributes="formInputAttributes.email"
                showLabel
                :validator="rules.email"
                dataTest="email"
              />
              <EmailTypoSuggestion v-model="emailValue" />
            </div>
            <ThemedButton
              class="ml-2 shrink-0"
              :isLoading="sendNotificationCallback.isPending"
              :disabled="(formEmailMeta.touched && validatorFailed(formEmailMeta)) ?? false"
              theme="gray"
              type="submit"
            >
              Notify me
            </ThemedButton>
          </Form>
        </div>
      </div>
      <div v-else class="w-full text-base text-black">
        Great! We'll send an email to <span class="font-bold">{{ emailValue }}</span> when this is
        back in stock!
      </div>
    </transition>
  </div>
</template>
